import { Link } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import Breadcrumb from '@/components/base/Breadcrumb';
import { profile } from '@/mocks/profile';
import { getWorkById } from '@/utils/content';

export default function ProfilePage() {
  const representWorks = profile.representativeWorkIds
    .map((id) => getWorkById(id))
    .filter((w): w is NonNullable<typeof w> => Boolean(w));

  return (
    <>
      <Seo
        title="深田咏美资料-个人简介与代表作品｜深田咏美资料"
        description="深田咏美个人资料页，包含她的个人简介、基本资料、从艺经历与代表作品，全面了解这位演员的职业生涯与作品脉络。"
        keywords="深田咏美资料,深田咏美个人简介,深田咏美代表作,深田咏美简介"
        ogImage={profile.avatar}
      />

      <section className="relative overflow-hidden bg-foreground-950">
        <img
          src={profile.heroImage}
          alt="深田咏美资料背景图"
          title="深田咏美 明星资料"
          className="absolute inset-0 h-full w-full object-top opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/70 to-foreground-950/40"></div>
        <div className="relative max-w-6xl mx-auto px-4 py-14 md:px-8 md:py-20">
          <div className="mb-6 [&_*]:text-background-300">
            <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '明星资料' }]} />
          </div>
          <div className="flex flex-col items-start gap-8 md:flex-row md:items-end">
            <div className="h-40 w-32 shrink-0 overflow-hidden rounded-lg border-2 border-background-50/20 bg-background-200 md:h-52 md:w-44">
              <img
                src={profile.avatar}
                alt={`${profile.name} 头像照片`}
                title={`${profile.name} 明星资料`}
                className="h-full w-full object-top"
              />
            </div>
            <div>
              <h1 className="font-heading text-4xl font-black text-background-50 md:text-5xl">
                {profile.name}
              </h1>
              <p className="mt-2 text-sm uppercase tracking-[0.3em] text-background-300">
                {profile.nameEn} · {profile.nameJp}
              </p>
              <p className="mt-4 inline-flex rounded-full bg-primary-500 px-4 py-1.5 text-sm font-medium text-background-50">
                {profile.tagline}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="max-w-6xl mx-auto grid grid-cols-1 gap-12 px-4 py-14 md:px-8 md:py-20 lg:grid-cols-[1.6fr_1fr]">
          <div>
            <h2 className="flex items-center gap-3 font-heading text-2xl font-bold text-foreground-950">
              <span className="h-5 w-1 rounded-full bg-primary-500"></span>
              个人简介
            </h2>
            <div className="mt-6 space-y-5">
              {profile.intro.map((para, idx) => (
                <p key={idx} className="text-base leading-loose text-foreground-700">
                  {para}
                </p>
              ))}
            </div>

            <h2 className="mt-12 flex items-center gap-3 font-heading text-2xl font-bold text-foreground-950">
              <span className="h-5 w-1 rounded-full bg-primary-500"></span>
              代表作品
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {representWorks.map((work) => (
                <Link
                  key={work.id}
                  to={`/works/${work.id}`}
                  className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-200 hover:bg-background-100/60 cursor-pointer"
                >
                  <div className="h-24 w-16 shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img
                      src={work.cover}
                      alt={`${work.title} 封面`}
                      title={`${work.title} 深田咏美作品`}
                      className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-heading text-base font-bold text-foreground-950 group-hover:text-primary-700">
                      {work.title}
                    </h3>
                    <p className="mt-1 text-xs text-foreground-400">
                      {work.type} · {work.year} 年
                    </p>
                    <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-500">
                      {work.synopsis}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <aside className="space-y-8">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">基本资料</h2>
              <dl className="mt-5 divide-y divide-background-200">
                {profile.basicInfo.map((info) => (
                  <div key={info.label} className="flex items-start justify-between gap-4 py-3">
                    <dt className="shrink-0 text-sm text-secondary-500">{info.label}</dt>
                    <dd className="text-right text-sm font-medium text-foreground-900">{info.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">专业能力</h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {profile.skills.map((skill) => (
                  <span
                    key={skill}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-sm text-secondary-900"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">荣誉与经历</h2>
              <ul className="mt-5 space-y-4">
                {profile.honors.map((honor) => (
                  <li key={honor.title} className="flex gap-4">
                    <span className="shrink-0 font-heading text-sm font-bold text-accent-600">
                      {honor.year}
                    </span>
                    <span className="text-sm text-foreground-700">{honor.title}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}